import { FolderIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { useToastService } from '../../services/toast-service.tsx'
import {
  getProjectMembershipsOptions,
  useAddProjectMemberships,
  useGetProjectMembershipsFromHalLink,
  useGetProjects,
} from '../../utils/api-client/api-hooks.ts'
import type { Project, Repository } from '../../utils/api-client/api-schemas.ts'
import { getTemplate } from '../../utils/api-client/templates.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { RadioGroup } from '../input/radio.tsx'
import { SearchInput } from '../input/search-input.tsx'
import { useShowProjectMutationError } from '../projects/admin-required-override.ts'
import {
  isProjectFullError,
  projectFullMessage,
  remainingCapacity,
  roomForMessage,
} from '../projects/project-capacity.ts'
import * as styles from '../projects/project-dialog.css.ts'
import * as cardStyles from '../projects/project-row-card.css.ts'

export type MoveRepositoryModalProps = {
  /** The repositories being moved — one row's action, or the whole selection. Never empty. */
  repositories: Pick<Repository, 'id' | 'full_name'>[]
  /** The project the repositories are leaving. */
  currentProject: Pick<Project, 'id' | 'name'>
  onClose: () => unknown
  onMoved: (destination: Project) => unknown
  /**
   * Returns focus to whatever opened this dialog. Radix restores to a `Dialog.Trigger`, and this
   * dialog has none — it mounts already open. @see useReturnFocus
   */
  onCloseAutoFocus?: (event: Event) => void
}

/** Picks the project a selection moves to. A repository belongs to one project, so this is a move. */
export const MoveRepositoryModal: React.FC<MoveRepositoryModalProps> = ({
  repositories,
  currentProject,
  onClose,
  onMoved,
  onCloseAutoFocus,
}) => {
  const { addToast } = useToastService()
  const queryClient = useQueryClient()
  // The chosen project itself, not its id: the search is the API's, so narrowing the list can take the
  // chosen row out of it, and narrowing the view must not un-choose it.
  const [destination, setDestination] = useState<Project>()
  const [search, setSearch] = useState('')
  const [fullError, setFullError] = useState<string>()
  // An organization can have hundreds of projects, so the radio list is searchable by name and the API
  // does the matching — this dialog needs the names and counts of what the search finds, not every project.
  const { projects, isPending: projectsPending, isError: projectsFailed } = useGetProjects({ name: search.trim() })
  const movingCount = repositories.length
  const destinations = useMemo(
    () =>
      projects
        .filter(project => project.id !== currentProject.id)
        // A destination that cannot take the whole selection is shown with what it can take rather than
        // hidden: the reader picked these repositories, and "not here" is only useful with a reason.
        .map(project => ({ project, remaining: remainingCapacity(project) })),
    [projects, currentProject.id]
  )

  const { data: destinationMemberships, isError: isDestinationFailed } = useGetProjectMembershipsFromHalLink({
    path: destination?._links.memberships.href,
    enabled: destination !== undefined,
  })
  const destinationCreate =
    destination && !isDestinationFailed ? getTemplate(destinationMemberships, 'create') : undefined
  const prefetchDestination = (project: Project) =>
    queryClient.query(getProjectMembershipsOptions(project._links.memberships.href)).catch(() => {})

  const showError = useShowProjectMutationError(
    movingCount === 1 ? 'Failed to move repository' : 'Failed to move repositories'
  )
  // The destination each request was sent for, held from the click. The destination list is the API's
  // answer to the current search, so the chosen project may not be in it; and a ref rather than state
  // because the hook-level `onSuccess` below still runs after the dialog has unmounted,
  // where a `mutate()`-level callback would be dropped along with the toast and `onMoved`.
  const requestedDestinations = useRef(new Map<string, Project>())
  const moveMutation = useAddProjectMemberships({
    onSuccess: (_data, { projectId }) => {
      const moved = requestedDestinations.current.get(projectId)
      if (!moved) {
        return
      }
      addToast({
        message:
          movingCount === 1 ? (
            <>
              {repositories[0]?.full_name} moved to &ldquo;{moved.name}&rdquo;
            </>
          ) : (
            <>
              {movingCount} repositories moved to &ldquo;{moved.name}&rdquo;
            </>
          ),
        variant: 'success',
      })
      onMoved(moved)
    },
    onError: (error, ...rest) => {
      // The cap is derived from a count the client was handed, so it can be out of date. The server is
      // the one that knows: keep the dialog open with what it said. The mutation refreshes the counts
      // behind it on settle, whatever the answer was.
      if (isProjectFullError(error)) {
        setFullError(projectFullMessage(error))
        // The refreshed counts will show this destination as unavailable, and the spec does not let an
        // unavailable destination stay chosen — un-choose it so Confirm cannot re-send the same 409.
        setDestination(undefined)
        return
      }
      showError(error, ...rest)
    },
  })
  const isPending = moveMutation.isPending
  const shownDestinationId = destinations.some(entry => entry.project.id === destination?.id)
    ? destination?.id
    : undefined

  const title = movingCount === 1 ? `Move ${repositories[0]?.full_name}` : `Move ${movingCount} repositories`
  const confirmLabel = movingCount === 1 ? 'Move repository' : `Move ${movingCount} repositories`
  const movingLabel = movingCount === 1 ? 'Moving repository' : 'Moving repositories'

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className={styles.title}>{title}</Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.help}>
              A repository belongs to one project, so moving{' '}
              {movingCount === 1 ? 'it here takes it' : 'them here takes them'} out of {currentProject.name}.
            </p>
          </Dialog.Description>

          <SearchInput ariaLabel="Search projects" value={search} onChange={setSearch} />

          {/* Radix owns the roving tab stop, the arrow keys and `aria-checked`, and skips the rows a
              selection has no room in. Hand-rolling that is what lost the group its only tab stop when
              a search filtered out the chosen destination. The row's content is phrasing-level because
              each item is a real button. */}
          <RadioGroup
            className={styles.scrollList}
            aria-label="Destination project"
            // Only a destination that is actually on screen is told to the group. The chosen one
            // deliberately outlives the search, and a checked item the group cannot render leaves it
            // with no tab stop at all — unreachable by keyboard. Confirm still knows the choice.
            value={shownDestinationId ?? ''}
            onValueChange={projectId => {
              setFullError(undefined)
              setDestination(destinations.find(entry => entry.project.id === projectId)?.project)
            }}
            disabled={isPending}
          >
            {destinations.map(({ project, remaining }) => {
              const isSelected = project.id === destination?.id
              const hasRoom = remaining >= movingCount
              return (
                <RadioGroupPrimitive.Item
                  key={project.id}
                  value={project.id}
                  disabled={!hasRoom}
                  onPointerEnter={() => prefetchDestination(project)}
                  onFocus={() => prefetchDestination(project)}
                  className={classNames(cardStyles.cardPick, cardStyles.cardOnDialog, {
                    [cardStyles.cardSelected]: isSelected,
                    [cardStyles.cardUnavailable]: !hasRoom,
                  })}
                >
                  <>
                    <span className={cardStyles.radio} aria-hidden />
                    <span className={cardStyles.badge}>
                      <FolderIcon size={20} weight="light" aria-hidden />
                    </span>
                    <span className={cardStyles.meta}>
                      <span className={cardStyles.name}>{project.name}</span>
                      <span className={cardStyles.sub}>
                        <span>
                          {project.repository_count} {pluralize(project.repository_count, 'repository', 'repositories')}
                        </span>
                        {project.description && (
                          <>
                            <i className={cardStyles.subSeparator}>·</i>
                            <span>{project.description}</span>
                          </>
                        )}
                        {!hasRoom && (
                          <>
                            <i className={cardStyles.subSeparator}>·</i>
                            <span>{roomForMessage(project.name, remaining)}</span>
                          </>
                        )}
                      </span>
                    </span>
                  </>
                </RadioGroupPrimitive.Item>
              )
            })}
            {destinations.length === 0 &&
              (projectsPending ? (
                <p className={styles.emptyNote}>Loading projects…</p>
              ) : projectsFailed ? (
                <p className={styles.errorNote} role="alert">
                  Failed to load projects.
                </p>
              ) : search.trim() === '' ? (
                <p className={styles.emptyNote}>
                  There is no other project to move {movingCount === 1 ? 'it' : 'them'} to.
                </p>
              ) : (
                <p className={styles.emptyNote}>No projects match &ldquo;{search.trim()}&rdquo;.</p>
              ))}
          </RadioGroup>

          {destination &&
            (isDestinationFailed ? (
              <p className={styles.errorNote} role="alert">
                Unable to check {destination.name}. Reload the page to try again.
              </p>
            ) : (
              (destinationCreate?.state === 'unavailable' || destinationCreate?.state === 'not-adopted') && (
                <p className={styles.emptyNote} role="status">
                  You can&apos;t move repositories into {destination.name}.
                </p>
              )
            ))}

          {fullError && (
            <p className={styles.errorNote} role="alert">
              {fullError}
            </p>
          )}

          <div className={styles.barEnd}>
            <GhostButton
              type="secondary"
              className={styles.outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              state={match({ isPending, destinationCreate })
                .with({ isPending: true }, () => 'loading' as const)
                .with({ destinationCreate: { state: 'loading' } }, () => 'loading' as const)
                .with({ destinationCreate: { state: 'available' } }, () => 'default' as const)
                .otherwise(() => 'disabled' as const)}
              onClick={() => {
                if (destination && destinationCreate?.state === 'available') {
                  requestedDestinations.current.set(destination.id, destination)
                  moveMutation.mutate({
                    projectId: destination.id,
                    template: { method: destinationCreate.method, target: destinationCreate.target },
                    repositoryIds: repositories.map(repository => repository.id),
                  })
                }
              }}
              loadingLabel={isPending ? movingLabel : 'Loading'}
            >
              {confirmLabel}
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
